import FormControl from '@mui/material/FormControl';
import OutlinedInput from '@mui/material/OutlinedInput';
import FormHelperText from '@mui/material/FormHelperText';
import InputLabel from '@mui/material/InputLabel';
import InputAdornment from '@mui/material/InputAdornment';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

const TextAreaField = props => {
    const {
        error = false,
        validationError = false,
        validationWarning = false,
        helperText,
        placeholder,
        label,
        value,
        maxCharsAllowed = 0,
        counter = {
            displayLength: false,
            displayMaxLength: false,
        },
        id = 'cw-id',
        size = 'large',
        disabled = false,
        skeleton = false,
        scrollable = false,
        onChange,
        onBlur,
        rows = 5,
    } = props;

    // eslint-disable-next-line no-nested-ternary
    const adornmentComponent = validationError ? (
        <ErrorOutlineIcon color="error" />
    ) : validationWarning ? (
        <WarningAmberIcon color="warning" />
    ) : null;

    const [textValue, setTextValue] = useState('');

    useEffect(() => {
        if (!textValue) {
            setTextValue(value);
        }
    }, [value]);

    const handleChange = e => {
        if (maxCharsAllowed > 0 && e.target.value.length > maxCharsAllowed) return;
        onChange(e.target.value);
    };

    const handleMaxCharsAllowed = e => {
        if (maxCharsAllowed > 0 && e.target.value.length > maxCharsAllowed) return;
        setTextValue(e.target.value);
    };

    const CounterComponent = ({ displayLength, displayMaxLength, length, maxLength }) => {
        return (
            <InputLabel>
                {displayLength && `${length}`}
                {displayLength && displayMaxLength && '/'}
                {displayMaxLength && `${maxLength}`}
            </InputLabel>
        );
    };

    return (
        <>
            <FormControl
                disabled={disabled}
                className="textAreaFormControl"
                size={size}
                error={error}
                value={textValue}
                skeleton={skeleton}
                onChange={handleChange}
                onBlur={onBlur}>
                <InputLabel htmlFor={id}>{label}</InputLabel>
                <CounterComponent
                    displayLength={counter.displayLength}
                    displayMaxLength={counter.displayMaxLength}
                    length={textValue?.length}
                    maxLength={maxCharsAllowed}
                />
                <OutlinedInput
                    multiline
                    minRows={rows}
                    placeholder={placeholder}
                    id={id}
                    aria-describedby="input"
                    endAdornment={
                        { adornmentComponent } && <InputAdornment position="end">{adornmentComponent}</InputAdornment>
                    }
                    value={textValue}
                    onChange={handleMaxCharsAllowed}
                    sx={scrollable ? { maxHeight: '200px', overflow: 'auto' } : {}}
                />
                <FormHelperText id="helper-text">{helperText}</FormHelperText>
            </FormControl>
        </>
    );
};

TextAreaField.propTypes = {
    error: PropTypes.bool,
    validationError: PropTypes.bool,
    validationWarning: PropTypes.bool,
    helperText: PropTypes.string,
    placeholder: PropTypes.string,
    label: PropTypes.string,
    value: PropTypes.string,
    maxCharsAllowed: PropTypes.number,
    counter: PropTypes.shape({
        displayLength: PropTypes.bool,
        displayMaxLength: PropTypes.bool,
    }),
    id: PropTypes.string,
    size: PropTypes.string,
    disabled: PropTypes.bool,
    skeleton: PropTypes.bool,
    scrollable: PropTypes.bool,
    onChange: PropTypes.func,
    onBlur: PropTypes.func,
};

TextAreaField.defaultProps = {
    error: false,
    validationError: false,
    validationWarning: false,
    helperText: ' ',
    placeholder: '',
    label: '',
    value: '',
    maxCharsAllowed: 0,
    counter: {
        displayLength: false,
        displayMaxLength: false,
    },
    id: 'cw-id',
    size: 'large',
    disabled: false,
    skeleton: false,
    scrollable: false,
    onChange: () => {},
    onBlur: () => {},
};

export default TextAreaField;
